import { addressList } from "@/mocks/addresses";
import SectionHeading from "@/pages/home/components/SectionHeading";

interface AddressSectionProps {
  onCopy: (text: string) => void;
}

const statusStyle: Record<string, { badge: string; dot: string }> = {
  available: { badge: "bg-accent-100 text-accent-800", dot: "bg-accent-500" },
  busy: { badge: "bg-primary-100 text-primary-800", dot: "bg-primary-500" },
  maintain: { badge: "bg-secondary-200 text-secondary-800", dot: "bg-secondary-500" },
};

export default function AddressSection({ onCopy }: AddressSectionProps) {
  return (
    <section id="lines" className="w-full bg-background-50 py-12 md:py-16">
      <div className="mx-auto w-full max-w-[1240px] px-4 md:px-6">
        <SectionHeading
          eyebrow="LIVE STATUS"
          title="最新可用地址 · 线路状态总览"
          desc="最后检测时间 10:24，状态每 5 分钟刷新一次；优先选择状态为「顺畅」且延迟较低的线路。"
          icon="ri-signal-tower-line"
        />

        <div className="overflow-hidden rounded-lg border border-background-200 bg-background-50">
          <div className="hidden grid-cols-[1.8fr_1.1fr_1fr_0.9fr_auto] items-center gap-4 border-b border-background-200 bg-background-100 px-5 py-3 text-xs font-bold uppercase tracking-wider text-foreground-500 md:grid">
            <span>线路名称 / 地址</span>
            <span>当前状态</span>
            <span>节点</span>
            <span>更新时间</span>
            <span className="text-right">操作</span>
          </div>

          <div className="divide-y divide-background-200">
            {addressList.map((line) => {
              const style = statusStyle[line.status] ?? statusStyle.available;
              return (
                <div
                  key={line.id}
                  className="flex flex-col gap-3 px-4 py-4 transition-colors hover:bg-background-100 md:grid md:grid-cols-[1.8fr_1.1fr_1fr_0.9fr_auto] md:items-center md:gap-4 md:px-5"
                >
                  <div className="flex items-start gap-3">
                    <span className="mt-0.5 flex h-9 w-9 shrink-0 items-center justify-center rounded-md bg-background-100 text-primary-600">
                      <i className="ri-server-line text-lg"></i>
                    </span>
                    <div>
                      <div className="flex flex-wrap items-center gap-2">
                        <span className="text-sm font-bold text-foreground-950">{line.name}</span>
                        {line.hot ? (
                          <span className="rounded-full bg-primary-100 px-2 py-0.5 text-[10px] font-bold text-primary-700">
                            推荐
                          </span>
                        ) : null}
                      </div>
                      <span className="mt-1 block font-mono text-xs text-foreground-600">{line.host}</span>
                    </div>
                  </div>

                  <div className="flex items-center gap-2">
                    <span
                      className={`inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 text-xs font-bold ${style.badge}`}
                    >
                      <span className={`h-1.5 w-1.5 rounded-full ${style.dot}`}></span>
                      {line.statusText}
                    </span>
                    <span className="font-mono text-xs text-foreground-500">{line.latency}</span>
                  </div>

                  <span className="text-xs text-foreground-600">{line.region}</span>
                  <span className="text-xs text-foreground-500">{line.updated}</span>

                  <div className="flex md:justify-end">
                    <button
                      type="button"
                      onClick={() => onCopy(line.host)}
                      className="flex cursor-pointer items-center gap-1.5 whitespace-nowrap rounded-md border border-background-300 px-3 py-2 text-xs font-bold text-foreground-800 transition-colors hover:border-primary-400 hover:bg-primary-50 hover:text-primary-700"
                    >
                      <i className="ri-file-copy-line text-sm"></i>
                      复制
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        <p className="mt-4 flex items-center gap-2 text-xs text-foreground-500">
          <i className="ri-information-line text-sm"></i>
          线路状态为演示用静态信息，仅用于展示页面结构，不代表真实可达性。
        </p>
      </div>
    </section>
  );
}